import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';

import type { HealthCategoryId } from '@shared/patient-intake-status/index.ts';

import { usePatientIntakeStatusContext } from '../hooks';
import { CustomStepCreator } from '../CustomStepCreator';
import { ActiveCategorySection } from './ActiveCategorySection.tsx';

type AddCustomStepProps = {
  activeHealthCategoryId: HealthCategoryId | null;
};

const AddCustomStep: React.FC<AddCustomStepProps> = ({ activeHealthCategoryId }) => {
  const { patientId, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [isAddingStep, setIsAddingStep] = useState(false);

  const handleAddCustomStep = () => {
    if (!activeHealthCategoryId) {
      return;
    }

    setIsAddingStep(true);
  };

  if (!isHealthcareProvider || !activeHealthCategoryId) {
    return null;
  }

  return (
    <>
      <Tooltip title={`Add a custom step for the patient's ${activeHealthCategoryId} consultation`}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddCustomStep}>
          Add Step
        </Button>
      </Tooltip>

      <CustomStepCreator
        patientId={patientId}
        healthCategoryId={activeHealthCategoryId}
        open={isAddingStep}
        onClose={() => setIsAddingStep(false)}
        onSuccess={() => setIsAddingStep(false)}
      />
    </>
  );
};

const Title: React.FC = () => {
  const { activeHealthCategoryId, activeHealthCategoryIds, isHealthcareProvider } = usePatientIntakeStatusContext();

  if (!activeHealthCategoryIds?.length) {
    return null;
  }

  if (!isHealthcareProvider) {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ textTransform: 'capitalize', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {activeHealthCategoryIds.length === 1 ? `${activeHealthCategoryId} Specific Requests` : 'Some Extra Requests'}
        </Typography>

        <AddCustomStep activeHealthCategoryId={activeHealthCategoryId} />
      </Stack>
    );
  }
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="h6" sx={{ textTransform: 'capitalize', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        {activeHealthCategoryIds.length === 1 ? `${activeHealthCategoryId} Intake` : 'Health Categories'}
      </Typography>

      <AddCustomStep activeHealthCategoryId={activeHealthCategoryId} />
    </Stack>
  );
};

export const HealthCategoriesSection: React.FC = () => {
  const { healthCategories, activeHealthCategoryId, activeHealthCategoryIds, setActiveHealthCategoryId } =
    usePatientIntakeStatusContext();

  const handleTabChange = (newValue: HealthCategoryId | null) => {
    if (newValue === activeHealthCategoryId) {
      return;
    }

    setActiveHealthCategoryId(newValue);
  };

  if (!healthCategories || !activeHealthCategoryIds?.length) {
    return null;
  }

  const hasMultipleCategories = activeHealthCategoryIds.length > 1;

  return (
    <Stack>
      <Title />

      {hasMultipleCategories && (
        <Tabs
          value={activeHealthCategoryId}
          onChange={(_, newValue) => handleTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {activeHealthCategoryIds.map((category) => (
            <Tab key={category} label={category} value={category} sx={{ textTransform: 'capitalize' }} />
          ))}
        </Tabs>
      )}

      <ActiveCategorySection activeHealthCategoryId={activeHealthCategoryId} />
    </Stack>
  );
};
